"use client";

import React, { useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import type {
  BaseEntity,
  SimpleTableConfig,
  SimpleFieldConfig,
} from "../../UniversalCRMTable/types";
import { useFieldFiltering } from "@/lib/hooks/search/useFieldFiltering";
import { FieldRow } from "./FieldRow";
import { useFieldEditing } from "../hooks/useFieldEditing";
import { CollapsibleSection } from "@/components/shared/CollapsibleSection";

type FieldEntry = [string, SimpleFieldConfig];
interface PanelSection {
  id: string;
  label: string;
  rows: FieldEntry[];
}

// ============================================================================
// INLINE EDITABLE INFO PANEL CONTENT (shared between panel and fullscreen modal)
// ============================================================================

interface InlineEditableInfoPanelContentProps<T extends BaseEntity> {
  item: T;
  config: SimpleTableConfig<T>;
  onInlineEdit?: (item: T) => Promise<void>;
  variant: "panel" | "fullscreen";
  displayTitle: string;
}

function InlineEditableInfoPanelContentComponent<T extends BaseEntity>({
  item,
  config,
  onInlineEdit,
  variant,
  displayTitle,
}: InlineEditableInfoPanelContentProps<T>) {
  const { filterFieldsByRole, roleName } = useFieldFiltering();

  const {
    editingField,
    editedItem,
    handleFieldEdit,
    handleSaveField,
    handleCancelEdit,
    handleFieldValueChange,
  } = useFieldEditing(item, onInlineEdit);

  const fieldsToDisplay = useMemo(() => {
    const roleFiltered = filterFieldsByRole(config.fields);
    // The inline panel is a FULL SUPERSET of editable fields: every editable
    // field renders here, INCLUDING fields that are also edited in the detail
    // header. Per product decision a user can edit any field from the properties
    // panel regardless of whether the header exposes the same editor — the two
    // surfaces intentionally overlap. Only truly non-editable fields are dropped
    // (`formOnly`, `editable: false`, `isReadOnlyDerived`) — this is an EDIT
    // panel (role-based read-only, e.g. client users, is NOT this: those fields
    // stay, rendered read-only).
    const entries = Object.entries(roleFiltered).filter(
      ([, fc]) => !fc.formOnly && fc.editable !== false && !fc.isReadOnlyDerived
    );
    const order = config.inlineInfoPanelFieldIds;
    if (order?.length) {
      const byId = new Map(entries);
      return order
        .map((id) => {
          const fc = byId.get(id);
          return fc ? ([id, fc] as const) : null;
        })
        .filter((row): row is [string, SimpleFieldConfig] => row != null);
    }
    return entries;
  }, [config.fields, config.inlineInfoPanelFieldIds, filterFieldsByRole]);

  // When the entity declares detail sections, group the panel fields into
  // collapsible sections (same metadata the mobile sectioned layout uses).
  // No section config → flat list (unchanged behavior).
  const sectionLayout = useMemo<PanelSection[] | null>(() => {
    const defs = config.mobileDetailSections;
    if (!defs?.length) return null;
    const unsectionedLabel = config.mobileDetailUnsectionedLabel ?? "Other";
    const byId = new Map<string, FieldEntry[]>(defs.map((d) => [d.id, []]));
    const other: FieldEntry[] = [];
    for (const entry of fieldsToDisplay) {
      const sid = entry[1].mobileDetailSectionId;
      const bucket = sid ? byId.get(sid) : undefined;
      if (bucket) bucket.push(entry);
      else other.push(entry);
    }
    const ordered: PanelSection[] = defs
      .map((d) => ({ id: d.id, label: d.label, rows: byId.get(d.id) ?? [] }))
      .filter((s) => s.rows.length > 0);
    if (other.length) ordered.push({ id: "__other__", label: unsectionedLabel, rows: other });
    return ordered.length > 0 ? ordered : null;
  }, [fieldsToDisplay, config.mobileDetailSections, config.mobileDetailUnsectionedLabel]);

  const canEdit = config.actions?.edit !== false && roleName !== "client_user";

  const isFullscreen = variant === "fullscreen";

  // Memoize callbacks to prevent FieldRow re-renders when editedItem changes
  const memoizedHandleFieldEdit = useCallback(
    (fieldId: string) => handleFieldEdit(fieldId),
    [handleFieldEdit]
  );

  const memoizedHandleSaveField = useCallback(
    (fieldId: string, newValue?: unknown) => handleSaveField(fieldId, newValue),
    [handleSaveField]
  );

  const memoizedHandleCancelEdit = useCallback(
    () => handleCancelEdit(),
    [handleCancelEdit]
  );

  const memoizedHandleFieldValueChange = useCallback(
    (fieldId: string, newValue: unknown) => handleFieldValueChange(fieldId, newValue),
    [handleFieldValueChange]
  );

  const renderRow = (fieldId: string, fieldConfig: SimpleFieldConfig) => (
    <FieldRow
      key={fieldId}
      fieldId={fieldId}
      fieldConfig={fieldConfig}
      value={editedItem[fieldId as keyof T]}
      isFieldEditing={editingField === fieldId}
      canEdit={canEdit}
      isFullscreen={isFullscreen}
      item={item}
      config={config}
      onFieldEdit={memoizedHandleFieldEdit}
      onSaveField={memoizedHandleSaveField}
      onCancelEdit={memoizedHandleCancelEdit}
      onFieldValueChange={memoizedHandleFieldValueChange}
    />
  );

  return (
    <div className={cn("space-y-4", isFullscreen && "max-h-[85vh] overflow-y-auto")}>
      {isFullscreen && (
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b pb-2">{displayTitle}</h2>
      )}
      {sectionLayout ? (
        <div className="space-y-1">
          {sectionLayout.map((section, index) => (
            <CollapsibleSection
              key={section.id}
              title={section.label}
              count={section.rows.length}
              defaultCollapsed={index > 0}
            >
              <div className="space-y-4">
                {section.rows.map(([fieldId, fieldConfig]) => renderRow(fieldId, fieldConfig))}
              </div>
            </CollapsibleSection>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {fieldsToDisplay.map(([fieldId, fieldConfig]) => renderRow(fieldId, fieldConfig))}
        </div>
      )}

      {/* Metadata */}
      <div className="pt-4 border-t space-y-2 text-xs text-gray-500">
        <div>ID: {item.id}</div>
        {(item as unknown as { created_at?: string }).created_at && (
          <div>
            Created:{" "}
            {new Date((item as unknown as { created_at: string }).created_at).toLocaleDateString()}
          </div>
        )}
        {(item as unknown as { updated_at?: string }).updated_at && (
          <div>
            Updated:{" "}
            {new Date((item as unknown as { updated_at: string }).updated_at).toLocaleDateString()}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// CUSTOM COMPARISON FUNCTION FOR MEMOIZATION
// ============================================================================

/**
 * Custom comparison function that only re-renders if the displayed fields have changed.
 * This prevents unnecessary re-renders when other properties of the item change.
 */
function arePropsEqual<T extends BaseEntity>(
  prevProps: InlineEditableInfoPanelContentProps<T>,
  nextProps: InlineEditableInfoPanelContentProps<T>
): boolean {
  // Always re-render if variant or displayTitle changes
  if (prevProps.variant !== nextProps.variant || prevProps.displayTitle !== nextProps.displayTitle) {
    return false;
  }

  // Always re-render if config changes
  if (prevProps.config !== nextProps.config) {
    return false;
  }

  // Always re-render if onInlineEdit callback changes
  if (prevProps.onInlineEdit !== nextProps.onInlineEdit) {
    return false;
  }

  // Check if any of the displayed fields have changed
  const prevFields = Object.keys(prevProps.config.fields);
  const nextFields = Object.keys(nextProps.config.fields);

  // If field keys changed, re-render
  if (prevFields.length !== nextFields.length) {
    return false;
  }

  // Check if any displayed field values have changed
  for (const fieldKey of prevFields) {
    const prevValue = prevProps.item[fieldKey as keyof T];
    const nextValue = nextProps.item[fieldKey as keyof T];

    // Use strict equality for primitives, deep comparison for objects
    if (typeof prevValue === "object" && typeof nextValue === "object") {
      if (JSON.stringify(prevValue) !== JSON.stringify(nextValue)) {
        return false;
      }
    } else if (prevValue !== nextValue) {
      return false;
    }
  }

  // Props are equal, don't re-render
  return true;
}

// ============================================================================
// MEMOIZED COMPONENT
// ============================================================================

export const InlineEditableInfoPanelContent = React.memo(
  InlineEditableInfoPanelContentComponent,
  arePropsEqual
) as typeof InlineEditableInfoPanelContentComponent;
